// WU-2.2 — persistent standing-instruction strip for the verification/keying
// station. Slim, info-tone, Safety-Orange left border; step-wide text first,
// then lane text. Live over WS (scope-matched merge). Collapsible to a chip;
// the collapsed flag is keyed by the CURRENT instruction ids, so a replaced
// instruction (new id) automatically re-expands with a brief pulse.
// Renders null when the scope carries no instructions. NEVER touches focus
// (F-077: the station's keyboard flow is sacred) — plain divs/buttons, no
// autofocus, no keydown handlers.
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useWsClient } from "@/lib/ws-provider";
import {
  queueInstructionsApi,
  type ScopedInstruction,
} from "@/features/admin/queue-instructions/api/client";

interface QueueInstructionPayload {
  action: "set" | "cleared";
  instruction: {
    id: string;
    stepType: string;
    jobTypeId: string | null;
    body?: string;
    createdAt?: string;
  };
}

function collapsedKey(instructions: ScopedInstruction[]): string {
  return `qi-collapsed:${instructions.map((i) => i.id).join(",")}`;
}

/**
 * B4/B5 (ff-web) — applies one WS delta (a "set" or "cleared" for a single
 * stepType/jobTypeId slot) to a list of instructions. Shared by the live WS
 * handler AND by the mount-fetch merge below, so a delta that lands while
 * the initial forScope() fetch is still in flight can be *replayed* on top
 * of the fetch's (possibly stale) result once it resolves — instead of the
 * fetch's blind replace silently reverting a WS "set" (B4) or resurrecting
 * an instruction a WS "cleared" already removed (B5).
 */
function applyInstructionDelta(
  list: ScopedInstruction[],
  evt: QueueInstructionPayload,
  sort: (l: ScopedInstruction[]) => ScopedInstruction[],
): ScopedInstruction[] {
  const scope = evt.instruction;
  const withoutSlot = list.filter(
    (i) => !(i.stepType === scope.stepType && i.jobTypeId === scope.jobTypeId),
  );
  if (evt.action === "cleared") return withoutSlot;
  return sort([
    ...withoutSlot,
    {
      id: scope.id,
      stepType: scope.stepType,
      jobTypeId: scope.jobTypeId,
      body: scope.body ?? "",
      createdAt: scope.createdAt ?? "",
    },
  ]);
}

function QueueInstructionStripImpl({
  stepType,
  jobTypeId,
}: {
  stepType: string;
  jobTypeId: string | null;
}) {
  const wsClient = useWsClient();
  const [instructions, setInstructions] = useState<ScopedInstruction[]>([]);
  const [expandedText, setExpandedText] = useState(false);
  const [pulse, setPulse] = useState(false);
  // Holds the in-flight pulse timeout so a second WS "set" within the 2s
  // window restarts the pulse instead of the first timer cutting it short,
  // and so unmount (scope change/navigation) doesn't setState on a gone
  // component.
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, []);

  // sessionStorage-backed collapse, keyed by the current instruction ids.
  const key = collapsedKey(instructions);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try {
      setCollapsed(sessionStorage.getItem(key) === "1");
    } catch {
      setCollapsed(false);
    }
  }, [key]);

  const sortByScope = useCallback(
    (list: ScopedInstruction[]) =>
      [...list].sort((a, b) =>
        (a.jobTypeId === null ? 0 : 1) - (b.jobTypeId === null ? 0 : 1),
      ),
    [],
  );

  // B4/B5 (ff-web) — deltas the WS handler applies live WHILE this scope's
  // forScope() fetch is still in flight. Reset at the top of the fetch
  // effect (new object per scope change, so a prior scope's deltas can
  // never leak into this one) and replayed onto the fetch's result when it
  // resolves, so neither a WS "set" nor a WS "cleared" that lands during
  // the race gets silently reverted by the fetch's response.
  const inFlightRef = useRef<{ deltas: QueueInstructionPayload[]; resolved: boolean }>({
    deltas: [],
    resolved: true,
  });

  useEffect(() => {
    let cancelled = false;
    const inFlight = { deltas: [] as QueueInstructionPayload[], resolved: false };
    inFlightRef.current = inFlight;
    queueInstructionsApi
      .forScope(stepType, jobTypeId ?? undefined)
      .then((res) => {
        if (cancelled) return;
        inFlight.resolved = true;
        let merged = sortByScope(res.instructions);
        for (const delta of inFlight.deltas) {
          merged = applyInstructionDelta(merged, delta, sortByScope);
        }
        setInstructions(merged);
      })
      .catch(() => {
        /* strip is best-effort; the queue card + next fetch cover it */
      });
    return () => {
      cancelled = true;
    };
  }, [stepType, jobTypeId, sortByScope]);

  useEffect(() => {
    if (!wsClient) return;
    const off = wsClient.onPayload("queue_instruction", (raw) => {
      const evt = raw as QueueInstructionPayload;
      if (!evt) return;
      const scope = evt.instruction;
      const matches =
        scope.stepType === stepType &&
        (scope.jobTypeId === null || scope.jobTypeId === jobTypeId);
      if (!matches) return;
      if (!inFlightRef.current.resolved) {
        inFlightRef.current.deltas.push(evt);
      }
      setInstructions((prev) => applyInstructionDelta(prev, evt, sortByScope));
      if (evt.action === "set") {
        if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
        setPulse(true);
        pulseTimerRef.current = setTimeout(() => setPulse(false), 2000);
      }
    });
    return off;
  }, [wsClient, stepType, jobTypeId, sortByScope]);

  if (instructions.length === 0) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        className="qi-chip"
        data-testid="qi-chip"
        title={instructions.map((i) => i.body).join(" • ")}
        aria-label={`Show queue instructions (${instructions.length})`}
        onClick={() => {
          try {
            sessionStorage.removeItem(key);
          } catch { /* best-effort */ }
          setCollapsed(false);
        }}
      >
        📌 {instructions.length}
      </button>
    );
  }

  return (
    <div
      className={`qi-strip${pulse ? " qi-strip--pulse" : ""}${expandedText ? " qi-strip--expanded" : ""}`}
      data-testid="qi-strip"
      role="note"
      aria-label="Queue instructions"
    >
      <span className="qi-strip-icon" aria-hidden="true">📌</span>
      <button
        type="button"
        className="qi-strip-text"
        onClick={() => setExpandedText((v) => !v)}
        title={expandedText ? "Collapse text" : "Show full text"}
      >
        {instructions.map((i) => (
          <span key={i.id} className="qi-strip-item">
            {i.jobTypeId === null && (
              <span className="qi-strip-scope">All {i.stepType}:</span>
            )}{" "}
            {i.body}
          </span>
        ))}
      </button>
      <button
        type="button"
        className="qi-strip-collapse"
        aria-label="Collapse queue instructions"
        onClick={() => {
          try {
            sessionStorage.setItem(key, "1");
          } catch { /* best-effort */ }
          setCollapsed(true);
        }}
      >
        ▾
      </button>
    </div>
  );
}

// VerificationStation re-renders on nearly every operator keystroke (it
// subscribes to many store slices); memoize since this strip's only inputs
// (stepType, jobTypeId) rarely change.
export const QueueInstructionStrip = memo(QueueInstructionStripImpl);
